import React from "react";

import { formatCampaignDates, type CampaignLane } from "../utils/dayMarkers";
import { channelLabel } from "./DayPips";
import { MarkerCardPortal, useMarkerCard } from "./MarkerCard";
import { SOURCE_COLOR } from "./ReportViz";

/** Each lane's height: a 3px bar centred in it, its hit area the whole lane. */
const LANE_HEIGHT = 10;
const END_PIP = 7;

interface CampaignLanesProps {
  lanes: ReadonlyArray<CampaignLane>;
  /** Campaigns of the range without a lane, named in each card. */
  hidden: number;
  /** The days on the chart's axis; a lane's ends are indexes into them. */
  length: number;
  /** The plot area's left and right distance from the chart box. */
  inset: { left: number; right: number };
}

const when = (lane: CampaignLane) =>
  lane.endDate ? formatCampaignDates(lane.startDate, lane.endDate) : `Running ${formatCampaignDates(lane.startDate, null)}`;

/** The range's Google Ads campaigns under a day chart's axis, one thin
 *  amber lane each, from its first day in the range to its last (to the
 *  chart's settled day while it runs). A start or end inside the range is a
 *  pip at that end; a side clipped by the range has none. Hover, focus or
 *  tap opens a card with the campaign's name, days and channel. */
export const CampaignLanes: React.FC<CampaignLanesProps> = ({ lanes, hidden, length, inset }) => {
  const card = useMarkerCard();
  if (lanes.length === 0) return null;
  const span = Math.max(length - 1, 1);
  const at = (index: number) => (index / span) * 100;
  const openLane = card.open ? lanes.find((lane) => lane.key === card.open?.key) : undefined;

  return (
    <div
      className="relative mt-1"
      style={{ marginLeft: inset.left, marginRight: inset.right, height: lanes.length * LANE_HEIGHT }}
    >
      {/* In the y axis's gutter, as its ticks: what the lanes are. */}
      <span
        aria-hidden="true"
        className="absolute right-full pr-[6px] text-[10px] leading-[10px] text-gray-400"
        style={{ top: 0 }}
      >
        Ads
      </span>
      {lanes.map((lane, i) => {
        const isOpen = card.open?.key === lane.key;
        const left = at(lane.from);
        const width = at(lane.to) - left;
        return (
          <button
            key={lane.key}
            type="button"
            aria-label={`Google Ads campaign ${lane.name}, ${when(lane)}`}
            className="absolute rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{
              top: i * LANE_HEIGHT,
              height: LANE_HEIGHT,
              left: `calc(${left}% - ${END_PIP / 2}px)`,
              width: `calc(${width}% + ${END_PIP}px)`,
            }}
            {...card.triggerProps(lane.key)}
          >
            <span
              className="absolute top-1/2 -translate-y-1/2"
              style={{
                left: END_PIP / 2,
                right: END_PIP / 2,
                height: isOpen ? 4 : 3,
                backgroundColor: SOURCE_COLOR.paid,
                borderRadius: `${lane.startsHere ? 2 : 0}px ${lane.endsHere ? 2 : 0}px ${lane.endsHere ? 2 : 0}px ${lane.startsHere ? 2 : 0}px`,
              }}
            />
            {lane.startsHere && <EndPip side="left" />}
            {lane.endsHere && <EndPip side="right" />}
          </button>
        );
      })}
      {card.open && openLane && (
        <MarkerCardPortal anchor={card.open.anchor} cardProps={card.cardProps} label={openLane.name}>
          <div className="flex items-start gap-2.5">
            <span className="shrink-0 pt-[5px]">
              <span
                className="block w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: SOURCE_COLOR.paid }}
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[12px] font-medium text-gray-900">{openLane.name}</span>
              <span className="block text-[11px] text-gray-500 tabular-nums">
                {when(openLane)}
                {openLane.channelType && ` · ${channelLabel(openLane.channelType)}`}
              </span>
            </span>
          </div>
          {hidden > 0 && (
            <p className="mt-1.5 text-[11px] text-gray-500">
              +{hidden} more campaign{hidden === 1 ? "" : "s"} in these days
            </p>
          )}
        </MarkerCardPortal>
      )}
    </div>
  );
};

const EndPip: React.FC<{ side: "left" | "right" }> = ({ side }) => (
  <span
    className="absolute top-1/2 -translate-y-1/2 rounded-full ring-2 ring-white"
    style={{
      [side]: 0,
      width: END_PIP,
      height: END_PIP,
      backgroundColor: SOURCE_COLOR.paid,
    }}
  />
);
